import React, { useEffect, useState } from "react";
import { adminApi } from "@/lib/adminApi";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AlertTriangle, Trash2 } from "lucide-react";
import { toast } from "sonner";

const TestData = () => {
  const [rows, setRows] = useState([]);
  const [sel, setSel] = useState({});
  const [loading, setLoading] = useState(true);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await adminApi.get("/test-data/candidates");
      setRows(r.data);
      setSel({});
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const selectedIds = Object.keys(sel).filter(k => sel[k]);
  const selCount = selectedIds.length;
  const allChecked = rows.length > 0 && rows.every(r => sel[r.id]);

  const toggleAll = () => {
    if (allChecked) setSel({});
    else setSel(Object.fromEntries(rows.map(r => [r.id, true])));
  };

  const purge = async () => {
    setBusy(true);
    try {
      const r = await adminApi.post("/test-data/purge", { user_ids: selectedIds, confirm: true });
      toast.success(`Deleted ${r.data.deleted.users} users + ${r.data.deleted.parcels} parcels + ${r.data.deleted.trips} trips`);
      setConfirmOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="p-8">
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-900"><Trash2 className="w-6 h-6 inline mr-2"/> Test data cleanup</h1>
          <p className="text-sm text-slate-500 mt-1">Users whose names match test patterns (TEST_ / test_ / E2E_) or are flagged <code>is_test_data</code>. Deleting a user also deletes their parcels, trips, deliveries, notifications, ratings, disputes and acceptances.</p>
        </div>
        <Button
          data-testid="btn-purge"
          disabled={selCount === 0}
          onClick={() => setConfirmOpen(true)}
          className="bg-red-600 hover:bg-red-700 disabled:bg-slate-300"
        >
          <Trash2 className="w-4 h-4 mr-1"/> Delete {selCount} selected
        </Button>
      </div>

      <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden">
        <div className="px-4 py-2 bg-slate-50 border-b border-slate-100 text-xs flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Checkbox checked={allChecked} onCheckedChange={toggleAll} data-testid="chk-all"/>
            <span>{rows.length} candidate{rows.length !== 1 ? "s" : ""}</span>
          </div>
        </div>
        <table className="w-full text-sm">
          <thead className="text-[11px] uppercase text-slate-500">
            <tr>
              <th className="w-10"></th>
              <th className="px-4 py-2 text-left">Name</th>
              <th className="px-4 py-2 text-left">Phone</th>
              <th className="px-4 py-2 text-left">Signup</th>
              <th className="px-4 py-2 text-right">Parcels</th>
              <th className="px-4 py-2 text-right">Trips</th>
              <th className="px-4 py-2 text-right">Deliveries</th>
              <th className="px-4 py-2 text-left">Match reason</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map(r => (
              <tr key={r.id} data-testid={`td-row-${r.id}`}>
                <td className="pl-4"><Checkbox checked={!!sel[r.id]} onCheckedChange={(v) => setSel({ ...sel, [r.id]: !!v })} data-testid={`td-chk-${r.id}`}/></td>
                <td className="px-4 py-2">{r.name || <em className="text-slate-400">Unnamed</em>}</td>
                <td className="px-4 py-2 text-slate-600">{r.phone}</td>
                <td className="px-4 py-2 text-xs tabular-nums text-slate-500">{r.created_at ? new Date(r.created_at).toLocaleDateString() : ""}</td>
                <td className="px-4 py-2 text-right tabular-nums">{r.parcels}</td>
                <td className="px-4 py-2 text-right tabular-nums">{r.trips}</td>
                <td className="px-4 py-2 text-right tabular-nums">{r.deliveries}</td>
                <td className="px-4 py-2 text-xs text-slate-500">{r.reason}</td>
              </tr>
            ))}
            {!loading && rows.length === 0 && <tr><td colSpan={8} className="text-center py-10 text-sm text-slate-400">No test data found. Dashboard figures are clean.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-red-600"/> Delete {selCount} test users?</DialogTitle>
            <DialogDescription>This will PERMANENTLY delete these users AND all of their parcels, trips, deliveries, notifications, ratings, disputes and acceptances. Written to the admin activity log. Cannot be undone.</DialogDescription>
          </DialogHeader>
          <div className="max-h-[220px] overflow-y-auto rounded-lg bg-slate-50 p-3 text-xs space-y-1">
            {rows.filter(r => sel[r.id]).map(r => (
              <div key={r.id}>{r.name || "(unnamed)"} <span className="text-slate-500">· {r.phone} · {r.parcels}p / {r.trips}t / {r.deliveries}d</span></div>
            ))}
          </div>
          <div className="flex items-center justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>Cancel</Button>
            <Button data-testid="btn-confirm-purge" disabled={busy} onClick={purge} className="bg-red-600 hover:bg-red-700">Yes, delete permanently</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TestData;
